import React, { useState } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, Users, DollarSign, Calendar, ArrowRight, Package, CreditCard, Battery } from 'lucide-react';

const SubscriptionTransitionDashboard = () => {
  const [selectedCompany, setSelectedCompany] = useState('Microsoft');
  const [activeTab, setActiveTab] = useState('market-winners');
  const [isChartFlipped, setIsChartFlipped] = useState(false);

  // Revenue comparison data
  const revenueComparison = [
    {
      company: 'Microsoft',
      beforeRevenue: 20,
      afterRevenue: 100,
      beforeYear: '2010',
      afterYear: '2024',
      unit: 'B',
      growthPercent: 400
    },
    {
      company: 'Netflix',
      beforeRevenue: 2.2,
      afterRevenue: 39,
      beforeYear: '2011',
      afterYear: '2024',
      unit: 'B',
      growthPercent: 1673
    },
    {
      company: 'Apple',
      beforeRevenue: 4,
      afterRevenue: 10,
      beforeYear: '2012',
      afterYear: '2025',
      unit: 'B',
      growthPercent: 150
    }
  ];

  // Combined subscriber growth timeline
  const allYears = [2007, 2010, 2011, 2015, 2017, 2018, 2019, 2020, 2021, 2023, 2025];
  const combinedSubscriberGrowth = allYears.map(year => {
    const dataPoint = { year };
    
    // Microsoft data
    if (year >= 2011) {
      if (year === 2011) dataPoint.Microsoft = 0;
      else if (year <= 2015) dataPoint.Microsoft = 50;
      else if (year <= 2018) dataPoint.Microsoft = 155;
      else if (year <= 2020) dataPoint.Microsoft = 258;
      else if (year <= 2023) dataPoint.Microsoft = 345;
      else dataPoint.Microsoft = 400;
    }
    
    // Netflix data
    if (year === 2007) dataPoint.Netflix = 7;
    else if (year <= 2010) dataPoint.Netflix = 20;
    else if (year <= 2015) dataPoint.Netflix = 70;
    else if (year <= 2020) dataPoint.Netflix = 203;
    else if (year <= 2023) dataPoint.Netflix = 238;
    else dataPoint.Netflix = 277;
    
    // Apple data
    if (year >= 2015) {
      if (year === 2015) dataPoint.Apple = 0;
      else if (year <= 2017) dataPoint.Apple = 27;
      else if (year <= 2019) dataPoint.Apple = 60;
      else if (year <= 2021) dataPoint.Apple = 78;
      else if (year <= 2023) dataPoint.Apple = 88;
      else dataPoint.Apple = 93;
    }
    
    return dataPoint;
  });

  // Company details
  const companyDetails = {
    Microsoft: {
      transitionYear: 2011,
      beforeModel: "Perpetual Office licenses",
      afterModel: "Microsoft 365 subscriptions",
      growthRate: "15-20%",
      valuation: "$3T+",
      keyBenefits: ["Predictable revenue", "AI integrations", "Enterprise lock-in"],
      challenges: ["Price hike backlash", "Migration complexity", "Google competition"],
      color: "#00BCF2"
    },
    Netflix: {
      transitionYear: 2007,
      beforeModel: "DVD rentals per-unit",
      afterModel: "Streaming subscriptions",
      growthRate: "10-15%",
      valuation: "$300B",
      keyBenefits: ["Global scale", "Original content", "Personalization"],
      challenges: ["Content costs", "Subscriber churn", "Market saturation"],
      color: "#E50914"
    },
    Apple: {
      transitionYear: 2015,
      beforeModel: "iTunes music purchases",
      afterModel: "Apple Music streaming",
      growthRate: "10-15%",
      valuation: "$3.5T",
      keyBenefits: ["Ecosystem synergy", "Bundled services", "Premium features"],
      challenges: ["Ownership loss", "Spotify rivalry", "DRM issues"],
      color: "#A8DADC"
    }
  };

  // Calculate statistics
  const revenueGrowthValues = [400, 1673, 150];
  const subscriberValues = [400, 277, 93];
  
  const avgRevenueGrowth = Math.round(revenueGrowthValues.reduce((a, b) => a + b, 0) / revenueGrowthValues.length);
  const medianRevenueGrowth = revenueGrowthValues.sort((a, b) => a - b)[Math.floor(revenueGrowthValues.length / 2)];
  
  const avgSubscribers = Math.round(subscriberValues.reduce((a, b) => a + b, 0) / subscriberValues.length);
  const medianSubscribers = subscriberValues.sort((a, b) => a - b)[Math.floor(subscriberValues.length / 2)];

  // PM vs AI PM Comparison Data
  const pmComparisonData = [
    { year: 'Yr 1', traditionalPM: 5000000, aiPM: 2000000, aiSubscribers: 2000000 },
    { year: 'Yr 2', traditionalPM: 5000000, aiPM: 4000000, aiSubscribers: 5900000 },
    { year: 'Yr 3', traditionalPM: 5000000, aiPM: 5000000, aiSubscribers: 10605000 },
    { year: 'Yr 4', traditionalPM: 5000000, aiPM: 6700000, aiSubscribers: 14774750 },
    { year: 'Yr 5', traditionalPM: 5000000, aiPM: 8400000, aiSubscribers: 18436013 },
    { year: 'Yr 6', traditionalPM: 5000000, aiPM: 9250000, aiSubscribers: 21764212 },
    { year: 'Yr 7', traditionalPM: 5000000, aiPM: 10695000, aiSubscribers: 24671001 },
    { year: 'Yr 8', traditionalPM: 5000000, aiPM: 12140000, aiSubscribers: 27177451 },
    { year: 'Yr 9', traditionalPM: 5000000, aiPM: 12862500, aiSubscribers: 29431079 },
    { year: 'Yr 10', traditionalPM: 5000000, aiPM: 14090750, aiSubscribers: 31355275 }
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-800 p-3 rounded-lg shadow-xl border border-gray-700">
          <p className="text-white font-semibold">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value.toLocaleString()}
              {entry.dataKey === 'aiSubscribers' ? ' subscribers' : ' subscribers'}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full text-gray-100">
      <div className="max-w-6xl mx-auto">

        {/* Tab Navigation */}
        <div className="flex justify-center mb-8">
          <div className="flex bg-gray-800/60 rounded-xl p-1 backdrop-blur-sm border border-gray-600/30">
            <button
              onClick={() => setActiveTab('market-winners')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                activeTab === 'market-winners'
                  ? 'bg-blue-500 text-white shadow-lg'
                  : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              Other Market Incumbents
            </button>
            <button
              onClick={() => setActiveTab('interstate-batteries')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                activeTab === 'interstate-batteries'
                  ? 'bg-blue-500 text-white shadow-lg'
                  : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              Interstate Batteries
            </button>
          </div>
        </div>

        {/* Market Winners Tab Content */}
        {activeTab === 'market-winners' && (
          <>
            {/* Company Transformations List */}
            <div className="mb-8">
          <div className="space-y-6">
            {/* Microsoft */}
            <div className="border-l-4 border-blue-400 pl-4">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-bold text-blue-400">Microsoft</h3>
                <div className="flex items-center gap-1 text-gray-400 text-sm">
                  <Calendar className="w-4 h-4" />
                  <span>2011</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-gray-700 rounded-lg p-3 flex-1">
                  <div className="flex items-center gap-2 text-gray-400 mb-1">
                    <Package className="w-3 h-3" />
                    <span className="text-xs">Before</span>
                  </div>
                  <p className="font-medium text-base">Perpetual Office licenses</p>
                </div>
                <ArrowRight className="w-5 h-5 text-purple-500" />
                <div className="bg-gradient-to-br from-gray-700 to-gray-600 rounded-lg p-3 flex-1 border border-purple-500/30">
                  <div className="flex items-center gap-2 text-purple-400 mb-1">
                    <CreditCard className="w-3 h-3" />
                    <span className="text-xs">After</span>
                  </div>
                  <p className="font-medium text-base">Microsoft 365 subscriptions</p>
                </div>
              </div>
            </div>

            {/* Netflix */}
            <div className="border-l-4 border-red-500 pl-4">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-bold text-red-500">Netflix</h3>
                <div className="flex items-center gap-1 text-gray-400 text-sm">
                  <Calendar className="w-4 h-4" />
                  <span>2007</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-gray-700 rounded-lg p-3 flex-1">
                  <div className="flex items-center gap-2 text-gray-400 mb-1">
                    <Package className="w-3 h-3" />
                    <span className="text-xs">Before</span>
                  </div>
                  <p className="font-medium text-base">DVD rentals per-unit</p>
                </div>
                <ArrowRight className="w-5 h-5 text-purple-500" />
                <div className="bg-gradient-to-br from-gray-700 to-gray-600 rounded-lg p-3 flex-1 border border-purple-500/30">
                  <div className="flex items-center gap-2 text-purple-400 mb-1">
                    <CreditCard className="w-3 h-3" />
                    <span className="text-xs">After</span>
                  </div>
                  <p className="font-medium text-base">Streaming subscriptions</p>
                </div>
              </div>
            </div>

            {/* Apple */}
            <div className="border-l-4 border-gray-300 pl-4">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-bold text-gray-300">Apple</h3>
                <div className="flex items-center gap-1 text-gray-400 text-sm">
                  <Calendar className="w-4 h-4" />
                  <span>2015</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-gray-700 rounded-lg p-3 flex-1">
                  <div className="flex items-center gap-2 text-gray-400 mb-1">
                    <Package className="w-3 h-3" />
                    <span className="text-xs">Before</span>
                  </div>
                  <p className="font-medium text-base">iTunes music purchases</p>
                </div>
                <ArrowRight className="w-5 h-5 text-purple-500" />
                <div className="bg-gradient-to-br from-gray-700 to-gray-600 rounded-lg p-3 flex-1 border border-purple-500/30">
                  <div className="flex items-center gap-2 text-purple-400 mb-1">
                    <CreditCard className="w-3 h-3" />
                    <span className="text-xs">After</span>
                  </div>
                  <p className="font-medium text-base">Apple Music streaming</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Revenue Before vs After */}
          <div className="bg-gray-800/50 rounded-xl p-4">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-400" />
              Revenue Impact: Before vs After
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={revenueComparison}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="company" stroke="#9CA3AF" fontSize={10} />
                <YAxis stroke="#9CA3AF" fontSize={10} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="beforeRevenue" name="Before ($B)" fill="#6B7280" />
                <Bar dataKey="afterRevenue" name="After ($B)" fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Subscriber Growth Timeline */}
          <div className="bg-gray-800/50 rounded-xl p-4">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-purple-400" />
              Subscriber Growth Timeline
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={combinedSubscriberGrowth}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="year" stroke="#9CA3AF" fontSize={10} />
                <YAxis stroke="#9CA3AF" fontSize={10} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="Microsoft" 
                  stroke="#00BCF2"
                  strokeWidth={3}
                  dot={{ fill: "#00BCF2", r: 4 }}
                  name="Microsoft (M)"
                  connectNulls={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="Netflix" 
                  stroke="#E50914"
                  strokeWidth={3}
                  dot={{ fill: "#E50914", r: 4 }}
                  name="Netflix (M)"
                  connectNulls={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="Apple" 
                  stroke="#A8DADC"
                  strokeWidth={3}
                  dot={{ fill: "#A8DADC", r: 4 }}
                  name="Apple (M)"
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
          </>
        )}

        {/* Interstate Batteries Tab Content */}
        {activeTab === 'interstate-batteries' && (
          <>
            {/* Interstate's Time to Shine */}
        <div className="mb-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">Interstate's Time to Shine</h2>
            <div className="flex items-center justify-center gap-2 text-gray-400 text-sm">
              <Calendar className="w-4 h-4" />
              <span>2025 - The Battery Revolution</span>
            </div>
          </div>
          
          <div className="flex items-center gap-6 mb-6">
            <div className="bg-gray-800/50 rounded-lg p-4 flex-1">
              <div className="flex items-center gap-2 text-gray-400 mb-2">
                <Package className="w-4 h-4" />
                <span className="text-sm">Before</span>
              </div>
              <p className="font-semibold text-white">Traditional battery sales</p>
              <p className="text-sm text-gray-300 mt-1">One-time purchases, no ongoing relationship</p>
            </div>
            
            <div className="flex flex-col items-center">
              <ArrowRight className="w-8 h-8 text-blue-400 mb-2" />
              <div className="bg-blue-500 p-2 rounded-full">
                <Battery className="w-6 h-6 text-white" />
              </div>
            </div>
            
            <div className="bg-gray-800/50 rounded-lg p-4 flex-1 border border-blue-500/30">
              <div className="flex items-center gap-2 text-blue-400 mb-2">
                <CreditCard className="w-4 h-4" />
                <span className="text-sm">After</span>
              </div>
              <p className="font-semibold text-white">Smart Battery Subscriptions</p>
              <p className="text-sm text-gray-300 mt-1">AI-powered monitoring, predictive maintenance, loyal customers</p>
            </div>
          </div>
          
          <div className="text-center mb-8">
            <p className="text-gray-300 font-medium">
              The next billion-dollar subscription transformation is happening in batteries
            </p>
          </div>

          {/* Flippable PM vs AI PM Chart */}
          <div className="bg-gray-800/50 rounded-xl border border-gray-600/30 relative" style={{ perspective: '1000px', minHeight: '500px' }}>
            <div 
              className="transition-transform duration-700 w-full h-full"
              style={{ 
                transformStyle: 'preserve-3d',
                transform: isChartFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
              }}
            >
              {/* Front of Card - Chart */}
              <div className="p-6 absolute inset-0" style={{ backfaceVisibility: 'hidden' }}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-white">The Subscriber Advantage</h3>
                  <button
                    onClick={() => setIsChartFlipped(!isChartFlipped)}
                    className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 px-3 py-1 rounded-lg text-sm transition-colors duration-300 border border-blue-500/30"
                  >
                    Show Details
                  </button>
                </div>
                
                <div className="bg-gray-900/50 rounded-xl p-4">
                  <h4 className="text-lg font-semibold mb-4 text-center text-white">Battery Sales: Traditional vs AI-Enabled Strategy</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={pmComparisonData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="year" stroke="#9CA3AF" fontSize={12} />
                      <YAxis yAxisId="left" stroke="#9CA3AF" fontSize={12} tickFormatter={(value) => `${(value / 1000000).toFixed(0)}M`} />
                      <YAxis yAxisId="right" orientation="right" stroke="#F59E0B" fontSize={12} tickFormatter={(value) => `${(value / 1000000).toFixed(0)}M`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="traditionalPM" 
                        stroke="#3B82F6"
                        strokeWidth={3}
                        dot={{ fill: "#3B82F6", r: 5 }}
                        name="Traditional PM Sales"
                        yAxisId="left"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="aiPM" 
                        stroke="#10B981"
                        strokeWidth={3}
                        dot={{ fill: "#10B981", r: 5 }}
                        name="AI PM Sales"
                        yAxisId="left"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="aiSubscribers" 
                        stroke="#F59E0B"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={{ fill: "#F59E0B", r: 4 }}
                        name="AI Subscribers (millions)"
                        yAxisId="right"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  
                  <div className="mt-4 text-center">
                    <p className="text-sm text-gray-400">
                      <span className="text-blue-400">■</span> Traditional PM: Flat 5M sales annually  
                      <span className="ml-4 text-green-400">■</span> AI PM: Growing subscriber-driven sales  
                      <span className="ml-4 text-yellow-400">- -</span> Active subscriber base
                    </p>
                  </div>
                </div>
              </div>

              {/* Back of Card - Detailed Explanation */}
              <div 
                className="absolute inset-0 p-6" 
                style={{ 
                  backfaceVisibility: 'hidden', 
                  transform: 'rotateY(180deg)' 
                }}
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-white">Two Product Managers, One Outcome</h3>
                  <button
                    onClick={() => setIsChartFlipped(!isChartFlipped)}
                    className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 px-3 py-1 rounded-lg text-sm transition-colors duration-300 border border-blue-500/30"
                  >
                    Show Chart
                  </button>
                </div>
                
                <div className="text-gray-300 text-sm leading-relaxed">
                  <p className="mb-4">
                    <span className="font-semibold text-white">The Scenario:</span> Interstate decides to create AI-enabled batteries. They plan to sell 5M a year. 
                    There's an <span className="text-blue-400 font-semibold">AI Product Manager</span> responsible for smart batteries. 
                    Sitting next to him is a <span className="text-gray-400 font-semibold">Standard Battery Product Manager</span> who also sells 5M batteries annually.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-blue-900/20 p-4 rounded-lg border border-blue-500/30">
                      <h4 className="font-semibold text-blue-400 mb-2">After 5 Years - AI Product Manager:</h4>
                      <ul className="text-xs space-y-1">
                        <li>• <span className="font-semibold">18.4M active subscribers</span></li>
                        <li>• Knows where they live and battery environment</li>
                        <li>• Tracks warranty data and battery health</li>
                        <li>• <span className="font-semibold text-blue-300">Confident selling 8.4M batteries in year 5</span></li>
                      </ul>
                    </div>
                    
                    <div className="bg-gray-800/30 p-4 rounded-lg border border-gray-600/30">
                      <h4 className="font-semibold text-gray-400 mb-2">After 5 Years - Standard PM:</h4>
                      <ul className="text-xs space-y-1">
                        <li>• <span className="font-semibold">Still needs to find 5M customers</span></li>
                        <li>• No ongoing relationship with past buyers</li>
                        <li>• No data on battery performance</li>
                        <li>• <span className="font-semibold text-gray-300">Same 5M target every year</span></li>
                      </ul>
                    </div>
                  </div>

                  <p className="text-center text-gray-400 italic text-sm">
                    After 10 years, the AI PM has 31M active subscribers and sells 14M+ batteries annually. 
                    The standard PM? Still chasing the same 5M target to random customers.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
          </>
        )}

      </div>
    </div>
  );
};

export default SubscriptionTransitionDashboard;